import { useRef } from "react";
import type { HlsVideoElement } from "hls-video-element";
import { useVideoAnalytics } from "@circle-react/components/AnalyticsTracker/useVideoAnalytics";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import type { MediaTranscoding } from "@circle-react/types/MediaTranscoding";
import { TranscriptNotification } from "@circle-react-uikit/TranscriptsPlayer/TranscriptNotification";
import { TranscriptPopup } from "@circle-react-uikit/TranscriptsPlayer/TranscriptPopup";
import { MediaSettingsOverlay } from "../MediaSettingsOverlay";
import { Player } from "./Player";
import { PlayerTheme } from "./PlayerTheme";
import { useMediaController } from "./hooks/useMediaController";
import { useVideoControls } from "./hooks/useVideoControls";
import { useVideoFullscreen } from "./hooks/useVideoFullscreen";
import { useVideoProgress } from "./hooks/useVideoProgress";
import { useVideoProgressStore } from "./hooks/useVideoProgressStore";
import { useVideoSource } from "./hooks/useVideoSource";
import { useVideoTranscript } from "./hooks/useVideoTranscript";
import type { VideoVariants } from "./types";

export interface VideoPlayerProps {
  className?: string;
  contentType?: string;
  downloadable?: boolean;
  downloadUrl?: string;
  mediaTranscoding?: MediaTranscoding;
  mediaTranscriptId?: number;
  posterUrl?: string;
  preload?: "none" | "metadata" | "auto";
  settingsEnabled?: boolean;
  signedId: string;
  src: string;
  variants?: VideoVariants;
}

export const VideoPlayer = ({
  className,
  contentType,
  downloadable = false,
  downloadUrl,
  mediaTranscoding,
  mediaTranscriptId,
  posterUrl,
  preload = "none",
  settingsEnabled = true,
  signedId,
  src,
  variants,
}: VideoPlayerProps) => {
  const videoRef = useRef<HlsVideoElement>(null);

  const { playbackSrc, isHls } = useVideoSource({
    mediaTranscoding,
    signedId,
    src,
    variants,
  });

  const { seek } = useVideoControls({ videoRef });
  const { currentTime } = useVideoProgress({ videoRef });

  const { clearStoredProgress } = useVideoProgressStore({
    currentTime,
    signedId,
    videoRef,
  });

  const {
    mediaTranscript,
    captionsUrl,
    cues,
    shouldDisplayTranscript,
    shouldDisplayNotification,
    updateMediaTranscript,
  } = useVideoTranscript({ mediaTranscriptId });

  useVideoAnalytics(videoRef, signedId);

  const { controller, dispatchMediaEvent } = useMediaController({
    videoRef,
  });

  const { handleVideoClick } = useVideoFullscreen({
    controller,
    dispatchMediaEvent,
  });

  return (
    <div className={classNames("group relative flex flex-col", className)}>
      <PlayerTheme downloadable={downloadable} downloadUrl={downloadUrl}>
        <Player
          className="max-h-full min-h-full min-w-full max-w-full object-contain object-center"
          src={playbackSrc}
          contentType={contentType}
          captionsUrl={captionsUrl}
          preload={preload}
          poster={posterUrl}
          isHls={isHls}
          onClick={handleVideoClick}
          onEnded={clearStoredProgress}
          videoRef={videoRef}
        />
      </PlayerTheme>
      {settingsEnabled && <MediaSettingsOverlay signedId={signedId} />}
      {shouldDisplayTranscript && (
        <TranscriptPopup
          onSeek={seek}
          onFullscreenSeek={() => {}}
          renderFullscreenPlayer={() => {}}
          onFullscreenToggle={() => {}}
          cues={cues}
          currentTime={currentTime}
        />
      )}
      {shouldDisplayNotification && (
        <TranscriptNotification
          url={src}
          onNewMediaTranscript={updateMediaTranscript}
          onMediaTranscriptUpdated={updateMediaTranscript}
          mediaTranscript={mediaTranscript}
          isLoading={false}
        />
      )}
    </div>
  );
};
